import * as Sentry from '@sentry/core';
import type * as SentryTypes from '@sentry/types';

import { HTTPError } from '../errors';
import { getCurrentScope } from '../sentry';
import { GenericTrace, type TraceOptions } from './generic';

Sentry.addTracingExtensions();

/**
 * Tracing implementation with binding with the Sentry SDK.
 */
export function SentryTrace(TraceImpl: typeof GenericTrace = GenericTrace) {
    return class SentryTrace extends TraceImpl {
        private sentryTransaction: SentryTypes.Transaction;

        constructor(name: string, opts: TraceOptions = {}) {
            super(name, opts);

            this.sentryTransaction = Sentry.startTransaction(
                {
                    name: this.name,
                    // We manage our distributed tracing and IDs ourselves and don't need Sentry to generate them
                    parentSpanId: this.parentSpanId ?? undefined,
                    spanId: this.spanId,
                },
                {
                    sampleRate: opts.sampleRate,
                }
            );

            getCurrentScope().setSpan(this.sentryTransaction);
        }

        protected end(error: Error | null) {
            super.end(error);

            if (process.env.GITBOOK_NO_ANALYTICS) {
                return;
            }

            const code = getErrorCode(error);
            this.sentryTransaction
                .setStatus(code ? Sentry.spanStatusfromHttpCode(code) : 'ok')
                .end();

            Sentry.addBreadcrumb({
                category: this.name,
                level: 'info',
                message: `${error ? 'failed' : 'succeeded'} in ${
                    this.endTime! - this.startTime!
                }ms`,
                data: this.labels,
            });
        }
    };
}

function getErrorCode(err: Error | null): number | undefined {
    if (!err) {
        return;
    }

    if (err instanceof HTTPError) {
        return err.statusCode;
    }

    return 500;
}
